import Vue from 'vue';
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
import { debounce } from 'vue-debounce';
import { navigateWindow } from '@/router/routerUtils';
import CartPreview from '@/modules/cart/CartPreview.vue';
import sidebar from '../Sidebar';
import TopPromoBars from './TopPromoBars.vue';
import MobileSidebar from '../components/MobileSidebar.vue';
import ParentMenu from '../components/ParentMenu.vue';
import MobileTopSearch from '../components/MobileTopSearch.vue';
import Navigation from '../components/Navigation.vue';
import SearchBar from '../components/SearchBar.vue';
import { $httpChangiRecommends } from '@/core/api';
import CookieConsent from '@/components/CookieConsent.vue';
import { ENV } from '@/env';
export default Vue.extend({
    props: {
        activeCategory: {
            type: String,
            default: '',
        },
        isHomePage: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        CartPreview,
        TopPromoBars,
        MobileSidebar,
        Navigation,
        SearchBar,
        CookieConsent,
        ParentMenu,
        MobileTopSearch,
    },
    data() {
        return {
            sidebar: sidebar,
            isNavSearchActive: false,
            searchText: '',
            searchResult: null,
            searchLoading: false,
            accountRewardPoints: 0,
            positionZero: true,
            buttonBackTop: false,
            menuOpen: false,
            stickySearchVisible: false,
            stickyThreshold: 0,
            isSearchVisible: true,
            isSearchVisibleOnMobile: false,
            revealProgress: 0,
            isMobile: false,
            _mq: null,
            _onMqChange: null,
        };
    },
    computed: {
        ...mapState('authModule', ['isLoggedIn']),
        ...mapState('wishlistModule', ['itemsWishlist', 'productNameToWishlist']),
        ...mapState('categoriesModule', ['menuCategories']),
        ...mapState('searchModule', ['query', 'recentSearches']),
        ...mapGetters('userModule', ['userName']),
        ...mapGetters('cartModule', ['cartItemsNumber']),
        typedMenuCategories() {
            return this.menuCategories;
        },
        isRootPage() {
            // covers both "/" path and a named "home" route if you use one
            return (this.$route?.path === '/') || (this.$route?.name === 'home');
        },
        stickySearchStyle() {
            // translate from 24px down to 0 as it reveals; opacity 0 -> 1; shadow grows in
            const t = 1 - this.revealProgress;
            const translate = Math.round(t * 24); // px
            const opacity = this.revealProgress.toFixed(3);
            const shadowOpacity = (0.06 * this.revealProgress).toFixed(3);
            return {
                transform: `translateY(${translate}px)`,
                opacity,
                boxShadow: `0 1px 12px rgba(17, 24, 39, ${shadowOpacity})`,
            };
        },
        ENV() {
            return ENV;
        },
    },
    created() {
        this.getCategories();
        if (window.location.pathname.includes('search/')) {
            this.searchText = this.query;
        }
        if (this.isLoggedIn) {
            try {
                $httpChangiRecommends.api.ChangiRecommendsRewardPointsAccountBanner().then((resp) => {
                    this.accountRewardPoints = resp.data.customerPointsBalance;
                });
            }
            catch (error) {
                console.error(error);
            }
        }
    },
    watch: {
        query(currentQuery) {
            if (currentQuery) {
                this.searchText = currentQuery;
            }
        },
    },
    mounted() {
        if (this.isHomePage) {
            this.isSearchVisible = false;
        }
        if (this.isLoggedIn) {
            this.getWishList();
            this.getUserData();
        }
        if (this.sidebar) {
            this.sidebar.enableSidebar();
        }
        setTimeout(() => {
            if ((this.$route.query.show == 'login' ||
                this.$route.query.show == 'password-changed') &&
                !this.isLoggedIn) {
                this.goToLogin();
            }
        }, 500);
        this.$nextTick(() => {
            this.computeStickyThreshold();
            this.onScroll();
        });
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.computeStickyThreshold);
        const mq = window.matchMedia('(max-width: 767.98px)');
        this._mq = mq;
        const setIsMobile = () => {
            this.isMobile = mq.matches;
            // Optional: if switching layouts while dropdown open, close it
            if (!this.isMobile && this.isNavSearchActive) {
                // keep or remove depending on desired behavior
                // this.closeNavSearch();
            }
        };
        setIsMobile();
        // Some browsers still use addListener/removeListener
        this._onMqChange = () => setIsMobile();
        if (typeof mq.addEventListener === 'function') {
            mq.addEventListener('change', this._onMqChange);
        }
        else {
            // @ts-ignore
            mq.addListener(this._onMqChange);
        }
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.computeStickyThreshold);
        if (this._mq && this._onMqChange) {
            if (typeof this._mq.removeEventListener === 'function') {
                this._mq.removeEventListener('change', this._onMqChange);
            }
            else {
                // @ts-ignore
                this._mq.removeListener(this._onMqChange);
            }
        }
    },
    destroyed() {
        this.sidebar.hideSidebar();
        this.sidebar.disableSidebar();
    },
    methods: {
        ...mapActions('categoriesModule', ['getCategories']),
        ...mapMutations('searchModule', ['setQuery', 'clearRecentSearches']),
        ...mapActions('authModule', ['logout', 'getUserData']),
        ...mapActions('wishlistModule', ['getWishList']),
        clamp(n, min, max) {
            return Math.max(min, Math.min(max, n));
        },
        computeStickyThreshold() {
            // Try to detect your banner/hero. Swap selectors to match your DOM.
            const candidates = [
                '.header-banner',
                '#headerBanner',
                '[data-cy="headerSearch"]',
                '.home-hero',
                '.hero',
            ];
            let bannerEl = null;
            for (const sel of candidates) {
                const found = document.querySelector(sel);
                if (found) {
                    bannerEl = found;
                    break;
                }
            }
            const buffer = 240; // feels more “seamless” than a big jump
            if (bannerEl) {
                const rect = bannerEl.getBoundingClientRect();
                const scrollTop = window.scrollY || window.pageYOffset;
                const bannerTop = rect.top + scrollTop;
                this.stickyThreshold = Math.max(0, bannerTop + bannerEl.offsetHeight + buffer);
            }
            else {
                // Fallback if no banner found
                this.stickyThreshold = 320;
            }
        },
        onScroll() {
            const y = window.scrollY || 0;
            // existing toggles
            this.positionZero = y === 0;
            this.buttonBackTop = y > 200;
            //header search display
            if (this.isHomePage) {
                this.isSearchVisible = (y > 300);
            }
            // Progressive reveal only on the root page; otherwise fully shown
            if (this.isRootPage) {
                if (!this.stickyThreshold)
                    this.computeStickyThreshold();
                // Start easing in a bit *before* the threshold and finish shortly *after*
                // so it feels like it slides up from below as you pass the banner.
                const start = this.stickyThreshold - 20; // begin pre-reveal
                const end = this.stickyThreshold + 50; // fully revealed by here
                const p = (y - start) / (end - start);
                this.revealProgress = this.clamp(p, 0, 1);
            }
            else {
                this.revealProgress = 1;
            }
        },
        openCart() {
            this.$refs.cartPreview?.toggle(true);
        },
        clearSearch() {
            this.searchResult = null;
            this.searchText = '';
            this.closeNavSearch();
        },
        goToLogin() {
            this.sidebar.hideSidebar();
            // preserve where the user is now, so you can return after callback
            const redirect = window.location.pathname + window.location.search + window.location.hash;
            // Save for callback to use later
            try {
                sessionStorage.setItem("ciam.postLoginRedirect", redirect);
            }
            catch (_) {
                // ignore storage errors (private mode / disabled storage)
            }
            const loginOrigin = process.env.VUE_APP_LOGIN_ORIGIN || "http://login.cr.local:8080";
            // Cross-host redirect (must be absolute)
            window.location.assign(`${loginOrigin}/ciam/login`);
        },
        goToAccount() {
            this.sidebar.hideSidebar();
            navigateWindow('account/dashboard');
        },
        goToCart() {
            this.sidebar.hideSidebar();
            navigateWindow('cart');
        },
        goToSearchPage() {
            navigateWindow(`search/${encodeURIComponent(this.searchText)}`);
        },
        goToWishlist() {
            navigateWindow('account/wishlist');
        },
        openMobileSearch() {
            this.isSearchVisibleOnMobile = true;
        },
        closeMobileSearch() {
            this.isSearchVisibleOnMobile = false;
            ;
        },
        openAuthModal() {
            this.sidebar.hideSidebar();
            this.$bvModal.show('authModal');
        },
        openNavSearch() {
            this.isNavSearchActive = true;
        },
        closeNavSearch() {
            this.isNavSearchActive = false;
        },
        searchTextChanged: debounce(function (value) {
            this.searchLoading = true;
            if (!value) {
                this.searchResult = null;
                this.searchLoading = false;
                return;
            }
            if (value.length < 3) {
                this.searchLoading = false;
                return;
            }
            this.setQuery(value);
            $httpChangiRecommends.api
                .ChangiRecommendsProductSearch({
                query: encodeURIComponent(value),
                autocompleteOnly: true,
            })
                .then((resp) => {
                this.searchResult = resp;
            })
                .catch((error) => {
                this.searchResult = { data: [], message: error.message };
            })
                .finally(() => {
                this.searchLoading = false;
            });
        }, 400),
        scrollToTop() {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
    },
});
